import React, { useMemo, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  getExpandedRowModel,
  flexRender,
  getSortedRowModel,
} from '@tanstack/react-table'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { Button } from '../ui/button'
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/16/solid'
import useGetMoneyPaths from '@/services/api/money-path/useGetMoneyPaths'
import DeleteMoneyPathModal from './DeleteMoneyPathModal'
import { Link } from 'react-router-dom'

const columnHelper = createColumnHelper()

function ColumnHeader({ children, className, ...rest }) {
  return (
    <div
      className={cn(['tw-font-medium tw-text-primary'], className)}
      {...rest}
    >
      {children}
    </div>
  )
}

export default function MoneyPathsTable() {
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: ({ table }) => (
          <ColumnHeader
            className={` group -mx-4 flex h-full min-w-[156px] items-center bg-gray-low  px-4 py-2 text-txt-secondary`}
          >
            Name
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => <div className="">{getValue()}</div>,
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('description', {
        header: ({ table }) => (
          <ColumnHeader
            className={` group -mx-4 flex h-full min-w-[156px] items-center bg-gray-low  px-4 py-2 text-txt-secondary`}
          >
            Description
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => <div className="">{getValue()}</div>,
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('current_path_date.percentage', {
        header: ({ table }) => (
          <ColumnHeader
            className={` group -mx-4 flex h-full  items-center bg-gray-low  px-4 py-2 text-txt-secondary`}
          >
            %
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => <div className="">{getValue()}</div>,
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('vault.name', {
        header: ({ table }) => (
          <ColumnHeader
            className={` group -mx-4 flex h-full min-w-[156px] items-center bg-gray-low  px-4 py-2 text-txt-secondary`}
          >
            Vault
          </ColumnHeader>
        ),
        enableSorting: true,
        cell: ({ row, getValue }) => <div className="">{getValue()}</div>,
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('action', {
        header: ({ table }) => (
          <ColumnHeader
            className={` group -mx-4 flex h-full items-center bg-gray-low  px-4 py-2 text-txt-secondary`}
          >
            {null}
          </ColumnHeader>
        ),
        enableSorting: false,
        cell: ({ row, getValue }) => (
          <div className="flex justify-end">
            <Link to={`/money-paths/${row.original.id}/edit`}>
              <Button iconOnly variant="ghost">
                <PencilSquareIcon />
              </Button>
            </Link>

            <DeleteMoneyPathModal key={row.original.id} id={row.original.id} />
          </div>
        ),
        footer: (info) => info.column.id,
      }),
    ],
    [],
  )

  const { data, status } = useGetMoneyPaths()

  const table = useReactTable({
    data: data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // getRowId: (row) => {
    //   return row.entity_type
    //     ? `${row.entity_type}-${row.id ?? 0}-${row.direction ?? 0}`
    //     : undefined
    // },
    // state: {
    //   columnPinning: {
    //     left: ['id'],
    //   },
    // },
    initialState: {
      //   columnPinning: {
      //     left: ['name'],
      //   },
      // expanded: true,
    },
  })

  if (status === 'pending') return 'Loading....'

  return (
    <div
      className={cn(
        'overflow-auto rounded-md border transition-all data-[busy]:pointer-events-none data-[busy]:animate-pulse data-[busy]:opacity-50',
      )}
    >
      <Table className="overflow-auto">
        <TableHeader className="sticky top-0 z-50">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="h-[60px]">
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className="w-[156px]- border-none bg-white"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              return (
                <TableRow
                  key={row.id}
                  className="group/row relative h-[52px] !opacity-100"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={`group/cell relative border-none bg-white transition-all group-hover/row:bg-opacity-15`}
                      data-first-col={
                        cell.column.getIsFirstColumn() ? true : undefined
                      }
                    >
                      <div
                        data-first-col={
                          cell.column.getIsFirstColumn() ? true : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              )
            })
          ) : (
            <TableRow>
              <TableCell className=" text-center">No results.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
